import React, { useState, useCallback } from 'react';
import { apiCall } from '../../utils/apiUtils';
import { showToast } from '../../utils/toastUtils';

// ── Payload builders ─────────────────────────────────
function buildAadhaarSinglePayload(fd) {
  const s = fd.signers[0];
  const coords = fd.signatureCoordinates.map((c) => ({
    page_number: c.page_number,
    x_coordinate: c.x_coordinate,
    y_coordinate: c.y_coordinate,
    width: c.width,
    height: c.height,
  }));

  return {
    signer_name: s.name,
    signer_email: s.email,
    signer_mobile: s.mobile,
    document_name: fd.fileName,
    document_data: fd.fileBase64,
    reason: fd.reason,
    days_to_expire: fd.daysToExpire,
    esign_type: 'EKYC',
    signature_coordinates: coords,
    redirect_url: fd.redirectUrl || undefined,
    need_name_match: fd.needNameMatch,
    percentage_name_match: fd.percentageNameMatch,
    notification_preferences: {
      notify_signer_on_complete: fd.notifySignerOnComplete ?? true,
      notify_requester_on_complete: fd.notifyRequesterOnComplete ?? true,
      notify_requester_on_progress: false,
    },
  };
}

function buildAadhaarMultiPayload(fd) {
  const signers = fd.signers.map((s, idx) => ({
    name: s.name,
    email: s.email,
    mobile: s.mobile,
    sequence: s.sequence || idx + 1,
    reason: fd.reason,
    esign_type: 'EKYC',
    days_to_expire: fd.daysToExpire,
    redirect_url: fd.redirectUrl || undefined,
    need_name_match: fd.needNameMatch,
    percentage_name_match: fd.percentageNameMatch,
    signature_coordinates: fd.signatureCoordinates
      .filter((c) => c.signerIndex === idx)
      .map((c) => ({
        page_number: c.page_number,
        x_coordinate: c.x_coordinate,
        y_coordinate: c.y_coordinate,
        width: c.width,
        height: c.height,
      })),
  }));

  return {
    document_name: fd.fileName,
    document_data: fd.fileBase64,
    signers,
    redirect_url: fd.redirectUrl || undefined,
    days_to_expire: fd.daysToExpire,
    notification_preferences: {
      notify_signer_on_complete: fd.notifySignerOnComplete ?? true,
      notify_requester_on_complete: fd.notifyRequesterOnComplete ?? true,
      notify_requester_on_progress: fd.notifyRequesterOnProgress ?? true,
    },
  };
}

function buildDigiSignPayload(fd) {
  const recipients = fd.signers.map((s, idx) => {
    const coord = fd.signatureCoordinates.find((c) => c.signerIndex === idx) || {};
    return {
      name: s.name,
      email: s.email,
      signing_order: s.sequence || idx + 1,
      signature_coordinate: {
        page_number: coord.page_number || 1,
        x_coordinate: coord.x_coordinate || 130,
        y_coordinate: coord.y_coordinate || 100,
        width: coord.width || 103,
        height: coord.height || 50,
      },
    };
  });

  return {
    signature_mode: fd.signatureMode,
    signature_flow: fd.signatureFlow,
    document_name: fd.fileName,
    document_data: fd.fileBase64,
    recipients,
    redirect_url: fd.redirectUrl || undefined,
    notification_preferences: {
      notify_signer_on_complete: fd.notifySignerOnComplete ?? true,
      notify_requester_on_complete: fd.notifyRequesterOnComplete ?? true,
      notify_requester_on_progress: fd.notifyRequesterOnProgress ?? true,
    },
  };
}

// ── Main ReviewSubmit Component ──────────────────────
const ReviewSubmit = ({ formData, onSuccess, onClose }) => {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { success, esignUrl, envelopeId, signers }
  const [error, setError] = useState('');

  const typeLabel =
    formData.signatureType === 'aadhaar_single'
      ? 'Aadhaar eSign (Single)'
      : formData.signatureType === 'aadhaar_multi'
      ? 'Aadhaar eSign (Multi)'
      : 'Digital Signature';

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setError('');

    try {
      let endpoint, payload;

      if (formData.signatureType === 'aadhaar_single') {
        endpoint = '/api/external/esign/send';
        payload = buildAadhaarSinglePayload(formData);
      } else if (formData.signatureType === 'aadhaar_multi') {
        endpoint = '/api/external/esign/multi-send';
        payload = buildAadhaarMultiPayload(formData);
      } else {
        endpoint = '/api/external/esign/digisign/initiate';
        payload = buildDigiSignPayload(formData);
      }

      const response = await apiCall(endpoint, {
        method: 'POST',
        data: payload,
      });

      setResult({
        success: true,
        envelopeId: response.envelope_id || response.document_id,
        esignUrl: response.esign_url || null,
        signers: response.signers || null,
        message: response.message,
      });

      // Save flagged signers to address book
      if (formData.saveFlags?.length) {
        formData.signers.forEach(async (s, idx) => {
          if (formData.saveFlags[idx]) {
            try {
              await fetch('/api/external/esign/signers', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  name: s.name,
                  email: s.email,
                  mobile: s.mobile || '',
                }),
              });
            } catch (e) { /* silent — save is best-effort */ }
          }
        });
      }

      showToast.success('eSign request sent successfully!');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to send request';
      setError(msg);
      showToast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }, [formData]);

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    showToast.success('Signing URL copied!');
  };

  // ── Success View ────────────────────────────────
  if (result?.success) {
    return (
      <div className="space-y-6 text-center py-4">
        <div className="w-16 h-16 mx-auto rounded-full bg-indigo-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Request Sent!</h3>
          <p className="text-sm text-gray-500 mt-1">{result.message}</p>
        </div>

        {/* Envelope ID */}
        <div className="bg-gray-50 rounded-lg p-3 text-sm">
          <span className="text-gray-500">Envelope ID: </span>
          <span className="font-mono font-medium text-gray-800">{result.envelopeId}</span>
        </div>

        {/* Signing URL (single signer) */}
        {result.esignUrl && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <p className="text-xs font-medium text-indigo-700 mb-2">Signing URL</p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={result.esignUrl}
                className="flex-1 px-3 py-2 text-xs font-mono bg-white border border-indigo-200 rounded-md truncate"
              />
              <button
                onClick={() => handleCopyUrl(result.esignUrl)}
                className="px-3 py-2 bg-indigo-600 text-white text-xs font-medium rounded-md hover:bg-indigo-700 transition-colors flex-shrink-0"
              >
                Copy
              </button>
            </div>
          </div>
        )}

        {/* Multi-signer URLs */}
        {result.signers && typeof result.signers === 'object' && (
          <div className="space-y-2 text-left">
            {Object.entries(result.signers).map(([key, info]) => (
              <div key={key} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">{info.name || key}</span>
                  <span className="text-xs text-gray-400">{info.status || 'pending'}</span>
                </div>
                {info.esign_url && (
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      readOnly
                      value={info.esign_url}
                      className="flex-1 px-2 py-1 text-xs font-mono bg-white border border-gray-200 rounded truncate"
                    />
                    <button
                      onClick={() => handleCopyUrl(info.esign_url)}
                      className="px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700"
                    >
                      Copy
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-center gap-3 pt-2">
          <button
            onClick={() => {
              onSuccess?.();
              onClose?.();
            }}
            className="px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-all"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── Review View ─────────────────────────────────
  return (
    <div className="space-y-5">
      <h3 className="text-base font-semibold text-gray-900">Review Request</h3>

      {/* Document */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500 text-xs font-medium block mb-0.5">Document</span>
            <span className="text-gray-800 font-medium">{formData.fileName}</span>
          </div>
          <div>
            <span className="text-gray-500 text-xs font-medium block mb-0.5">Type</span>
            <span className="text-gray-800 font-medium">{typeLabel}</span>
          </div>
          <div>
            <span className="text-gray-500 text-xs font-medium block mb-0.5">Expires In</span>
            <span className="text-gray-800 font-medium">{formData.daysToExpire} days</span>
          </div>
          <div>
            <span className="text-gray-500 text-xs font-medium block mb-0.5">Reason</span>
            <span className="text-gray-800 font-medium">{formData.reason}</span>
          </div>
        </div>
      </div>

      {/* Signers */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">
          {formData.signers.length === 1 ? 'Signer' : `Signers (${formData.signers.length})`}
        </h4>
        <div className="space-y-2">
          {formData.signers.map((s, idx) => {
            const boxCount = formData.signatureCoordinates.filter(
              (c) => c.signerIndex === idx
            ).length;
            return (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-800">{s.name}</p>
                  <p className="text-xs text-gray-400">{s.email}{s.mobile ? ` | ${s.mobile}` : ''}</p>
                </div>
                <span className="text-xs text-gray-500">
                  {boxCount} signature {boxCount === 1 ? 'field' : 'fields'}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Advanced summary */}
      {(formData.needNameMatch || formData.redirectUrl) && (
        <div className="text-xs text-gray-500 space-y-1">
          {formData.needNameMatch && (
            <p>Name matching enabled ({formData.percentageNameMatch}% threshold)</p>
          )}
          {formData.redirectUrl && <p>Redirect: {formData.redirectUrl}</p>}
        </div>
      )}

      {/* Notification preferences summary (only show disabled ones) */}
      {(!formData.notifySignerOnComplete || !formData.notifyRequesterOnComplete ||
        (formData.signatureType !== 'aadhaar_single' && !formData.notifyRequesterOnProgress)) && (
        <div className="text-xs text-gray-500 space-y-1">
          {!formData.notifySignerOnComplete && <p>Signer completion email: disabled</p>}
          {!formData.notifyRequesterOnComplete && <p>Requester completion email: disabled</p>}
          {formData.signatureType !== 'aadhaar_single' && !formData.notifyRequesterOnProgress && (
            <p>Progress updates: disabled</p>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Submit */}
      <div className="flex justify-end pt-2">
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="px-6 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {submitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Sending...
            </>
          ) : (
            <>
              Send eSign Request
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ReviewSubmit;
